// src/pages/MyReservations.tsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaCalendarCheck,
  FaArrowRight,
  FaMapMarkerAlt,
  FaSyncAlt,
  FaReceipt,
  FaExclamationTriangle,
  FaEnvelope,
  FaPhone,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  dateTime,
  displayStatus,
  fetchReservations,
  getReservationPrice,
  getTravelerSession,
  moneyCad,
} from '../lib/travelerData';
import { useTransactionI18n } from '../lib/transactionI18n';
import ForfeoReservationPassPanel from '../components/network/ForfeoReservationPassPanel';
import ReserveUserBookingsPanel from '../components/reserve/ReserveUserBookingsPanel';
import { isForfeoReserveUserExperienceEnabled } from '../lib/forfeoReserveUserExperienceClient';

type LoadState = 'loading' | 'ready' | 'error';

const MyReservations: React.FC = () => {
  const navigate = useNavigate();
  const { tx, orderStatus } = useTransactionI18n();
  const [state, setState] = useState<LoadState>('loading');
  const [reservations, setReservations] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const reserveUserExperienceEnabled = isForfeoReserveUserExperienceEnabled();

  const loadReservations = async () => {
    setState('loading');
    setError(null);

    try {
      const traveler = await getTravelerSession();
      if (!traveler) {
        navigate('/login?redirect=/mes-reservations');
        return;
      }

      setEmail(traveler.email);
      const result = await fetchReservations(traveler.uid, traveler.email);
      setReservations(result.data);
      setState('ready');
    } catch (err: any) {
      console.error('MyReservations error:', err);
      setError(tx('reservationsLoadError'));
      setState('error');
    }
  };

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />
      <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest mb-4">
                <FaCalendarCheck /> {tx('reservationsBadge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-slate-900">{tx('reservationsTitle')}</h1>
              <p className="text-slate-500 mt-2">{tx('reservationsSubtitle')}</p>
            </div>
            <button
              onClick={loadReservations}
              className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 px-5 py-3 rounded-2xl font-bold text-slate-700 hover:bg-slate-100"
            >
              <FaSyncAlt /> {tx('refresh')}
            </button>
          </div>

          {email && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500">
              <FaEnvelope /> {tx('account')} : {email}
            </div>
          )}

          {reserveUserExperienceEnabled && (
            <ReserveUserBookingsPanel className="mb-8" />
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-100 text-red-700 p-4 rounded-2xl font-semibold flex items-start gap-3">
              <FaExclamationTriangle className="mt-1 shrink-0" /> {error}
            </div>
          )}

          {state === 'loading' ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 font-bold">{tx('loadingReservations')}</p>
            </div>
          ) : reservations.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <FaCalendarCheck className="mx-auto text-5xl text-slate-300 mb-5" />
              <h2 className="text-2xl font-black text-slate-900 mb-2">{tx('noReservationsTitle')}</h2>
              <p className="text-slate-500 mb-8">{tx('noReservationsSubtitle')}</p>
              <Link to="/last-call" className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl font-black hover:bg-red-700">
                {tx('discoverLastCall')} <FaArrowRight />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {reservations.map((r) => {
                const business = r.business || {};
                const partnerName = business.name || r.partner_name || tx('partnerBusiness');
                const address = business.address || business.region || business.city || '';
                const mapsQuery = encodeURIComponent(`${partnerName} ${address}`.trim());
                const price = getReservationPrice(r);

                return (
                  <article key={r.id} className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-xl transition">
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4 mb-5">
                        <div>
                          <h2 className="text-xl font-black text-slate-900">{r.deal_title || r.title || tx('reservationForfeo')}</h2>
                          <p className="text-sm text-slate-500 mt-1">{dateTime(r.slot_start || r.booking_date || r.created_at)}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-green-50 text-green-700 border border-green-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest">
                          {orderStatus(r.status || r.payment_status || displayStatus(r.status || r.payment_status))}
                        </span>
                      </div>

                      <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 mb-5">
                        <div className="flex items-center gap-2 font-black text-slate-900 mb-1">
                          <FaMapMarkerAlt className="text-red-500" /> {partnerName}
                        </div>
                        <p className="text-sm text-slate-500">{address || '—'}</p>
                        {business.phone && (
                          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2"><FaPhone /> {business.phone}</p>
                        )}
                      </div>

                      <ForfeoReservationPassPanel
                        compact
                        id={r.id}
                        kind="reservation"
                        title="Pass Last Call prêt pour l’app"
                        subtitle="Retrouvez cette réservation, son statut et son futur QR sécurisé directement dans le wallet Forfeo."
                        className="mb-5"
                      />

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-600">
                          <FaReceipt className="text-slate-400" /> {moneyCad(price)}
                        </div>
                        <div className="flex gap-2">
                          {mapsQuery && mapsQuery.length > 0 && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200"
                            >
                              GPS
                            </a>
                          )}
                          <Link to={`/reservation/${r.id}`} className="px-4 py-2 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 inline-flex items-center gap-2">
                            Détails <FaExternalLinkAlt className="text-[10px]" />
                          </Link>
                          <Link to="/service-client" className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800">
                            Aide
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyReservations;
